import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LockHolidayRequest, LockHolidayResponse } from "api/ApiClient";
import axios from "axios";
import React, { useState } from "react";
import { BASE_API_URL } from "store/Me";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  statusBadgeOutlineClass,
  statusBadgeVariantFromColor,
} from "shared/utils/statusChipColor";

const HOLIDAYS_QUERY_KEY = (lockId: string) => ["locks", "holidays", lockId];

const HOLIDAY_ACTIONS = [
  { value: "pass", label: "Pass" },
  { value: "sec", label: "Secure" },
  { value: "rstrctSec", label: "Restricted Secure" },
];

const HOLIDAY_ACTION_DESCRIPTIONS: Record<string, string> = {
  pass: "Unlock during this holiday window.",
  sec: "Keep lock secure during this holiday window.",
  rstrctSec: "Keep lock secure and require elevated access.",
};

const HOLIDAY_PRESETS = [
  { key: "laterToday", label: "Later Today" },
  { key: "tomorrowMorning", label: "Tomorrow Morning" },
  { key: "weekend", label: "This Weekend" },
  { key: "oneDay", label: "One Day" },
] as const;

type HolidayPresetKey = (typeof HOLIDAY_PRESETS)[number]["key"];

function formatAction(action: string | undefined): string {
  const found = HOLIDAY_ACTIONS.find((a) => a.value === action);
  return found?.label ?? action ?? "—";
}

function parseDateInput(value: Date | string | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(`${value}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatUtcDate(value: Date | string | undefined): string {
  const date = parseDateInput(value);
  if (!date) return "—";
  return date.toLocaleString();
}

function formatRelativeDate(value: Date | string | undefined): string {
  const date = parseDateInput(value);
  if (!date) return "";

  const diffMs = date.getTime() - Date.now();
  const minutes = Math.max(1, Math.round(Math.abs(diffMs) / 60000));
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (minutes < 60) {
    return diffMs >= 0 ? `In ${minutes} min` : `${minutes} min ago`;
  }

  if (hours < 24) {
    return diffMs >= 0 ? `In ${hours} hr` : `${hours} hr ago`;
  }

  return diffMs >= 0 ? `In ${days} day` : `${days} day ago`;
}

function getHolidayStatus(
  holiday: Pick<LockHolidayResponse, "startDateTime" | "endDateTime">,
): { label: string; color: "success" | "primary" | "default" } {
  const now = new Date();
  const start = parseDateInput(holiday.startDateTime);
  const end = parseDateInput(holiday.endDateTime);

  if (!start || !end) {
    return { label: "Unknown", color: "default" };
  }

  if (now < start) {
    return { label: "Upcoming", color: "primary" };
  }

  if (now >= start && now <= end) {
    return { label: "Active", color: "success" };
  }

  return { label: "Expired", color: "default" };
}

function toDatetimeLocalInput(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Converts a local datetime-local input value to a Date
function parseLocalDatetime(value: string): Date {
  return new Date(value);
}

// Formats a date for a datetime-local input value (YYYY-MM-DDTHH:MM)
function toDatetimeLocal(value: Date | string | undefined): string {
  const date = parseDateInput(value);
  if (!date) return "";
  return toDatetimeLocalInput(date);
}

function getPresetRange(preset: HolidayPresetKey): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  if (preset === "laterToday") {
    start.setHours(start.getHours() + 2, 0, 0, 0);
    end.setHours(start.getHours() + 4, 0, 0, 0);
  }

  if (preset === "tomorrowMorning") {
    start.setDate(start.getDate() + 1);
    start.setHours(8, 0, 0, 0);
    end.setTime(start.getTime());
    end.setHours(12, 0, 0, 0);
  }

  if (preset === "weekend") {
    const day = now.getDay();
    const daysToSaturday = day === 6 ? 0 : (6 - day + 7) % 7;
    start.setDate(now.getDate() + daysToSaturday);
    start.setHours(0, 0, 0, 0);
    end.setTime(start.getTime());
    end.setDate(end.getDate() + 2);
    end.setHours(23, 59, 0, 0);
  }

  if (preset === "oneDay") {
    start.setDate(start.getDate() + 1);
    start.setHours(0, 0, 0, 0);
    end.setTime(start.getTime());
    end.setDate(end.getDate() + 1);
    end.setHours(0, 0, 0, 0);
  }

  return {
    start: toDatetimeLocalInput(start),
    end: toDatetimeLocalInput(end),
  };
}

export default function LockHolidays({
  lockId,
  siteTimeZone,
}: {
  lockId: string;
  siteTimeZone?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] =
    useState<LockHolidayResponse | null>(null);
  const [copySourceHoliday, setCopySourceHoliday] =
    useState<LockHolidayResponse | null>(null);

  const { data, isError, isLoading } = useGetLockHolidays(lockId);
  const showEmptyState = !!data && data.length === 0;
  const timezone = siteTimeZone || "America/Chicago";
  const sortedHolidays = React.useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      const aStart = parseDateInput(a.startDateTime)?.getTime() ?? 0;
      const bStart = parseDateInput(b.startDateTime)?.getTime() ?? 0;
      return aStart - bStart;
    });
  }, [data]);

  const openCreate = () => {
    setEditingHoliday(null);
    setCopySourceHoliday(null);
    setModalOpen(true);
  };

  const openEdit = (holiday: LockHolidayResponse) => {
    setEditingHoliday(holiday);
    setCopySourceHoliday(null);
    setModalOpen(true);
  };

  const openCopy = (holiday: LockHolidayResponse) => {
    setEditingHoliday(null);
    setCopySourceHoliday(holiday);
    setModalOpen(true);
  };

  if (isError) return <div>Error loading holidays</div>;
  if (isLoading) return <div>Loading...</div>;

  return (
    <div style={{ paddingBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <p style={{ margin: 0, color: "#666", fontSize: 13 }}>
          Use holiday scheduling to override automation for specific date
          ranges, or to require elevated access for a specified period. Changes
          take effect on the next lock sync. Times are shown in {timezone}.
        </p>
        {!showEmptyState ? (
          <Button type="button" onClick={openCreate}>
            Create Holiday
          </Button>
        ) : null}
      </div>

      {showEmptyState ? (
        <div
          style={{
            border: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            borderRadius: 8,
            padding: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, color: "#111827", marginBottom: 4 }}>
              No holidays yet
            </div>
            <div style={{ color: "#6b7280", fontSize: 13 }}>
              Add a holiday schedule to override lock behavior for specific dates.
            </div>
          </div>
          <Button type="button" onClick={openCreate}>
            Create Holiday
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3">
              <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                  <th scope="col">Start</th>
                  <th scope="col">End</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
                {sortedHolidays.map((holiday) => (
                  <HolidayRow
                    key={holiday.id}
                    holiday={holiday}
                    lockId={lockId}
                    onEdit={() => openEdit(holiday)}
                    onCopy={() => openCopy(holiday)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <HolidayFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        lockId={lockId}
        holiday={editingHoliday}
        copySourceHoliday={copySourceHoliday}
        holidays={data ?? []}
        timezone={timezone}
      />
    </div>
  );
}

function HolidayRow({
  holiday,
  lockId,
  onEdit,
  onCopy,
}: {
  holiday: LockHolidayResponse;
  lockId: string;
  onEdit: () => void;
  onCopy: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: deleteHoliday, isPending: isDeleting } = useMutation({
    mutationFn: () =>
      axios.delete(
        `${BASE_API_URL}/api/Locks/${lockId}/holidays/${holiday.id}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY(lockId) });
      setConfirmOpen(false);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Error deleting holiday.";
      setDeleteError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });
  const holidayStatus = getHolidayStatus(holiday);

  return (
    <>
      <tr>
        <td>
          <Badge
            className={
              holidayStatus.label === "Expired"
                ? statusBadgeOutlineClass(holidayStatus.color)
                : undefined
            }
            variant={
              holidayStatus.label === "Expired"
                ? "outline"
                : statusBadgeVariantFromColor(holidayStatus.color)
            }
          >
            {holidayStatus.label}
          </Badge>
        </td>
        <td>{formatAction(holiday.action)}</td>
        <td>
          <div>{formatUtcDate(holiday.startDateTime)}</div>
          <div style={{ color: "#6b7280", fontSize: 12 }}>
            {formatRelativeDate(holiday.startDateTime)}
          </div>
        </td>
        <td>
          <div>{formatUtcDate(holiday.endDateTime)}</div>
          <div style={{ color: "#6b7280", fontSize: 12 }}>
            {formatRelativeDate(holiday.endDateTime)}
          </div>
        </td>
        <td>
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="sm" type="button" variant="outline" onClick={onEdit}>
              Edit
            </Button>
            <Button size="sm" type="button" variant="outline" onClick={onCopy}>
              Copy
            </Button>
            <Button
              className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                setDeleteError(null);
                setConfirmOpen(true);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              Delete
            </Button>
          </div>
        </td>
      </tr>

      <Dialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          showCloseButton={false}
          className="sm:max-w-xs"
        >
          <DialogHeader>
            <DialogTitle>Delete Holiday</DialogTitle>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              {deleteError}
            </Alert>
          )}
          Are you sure you want to delete this <strong>{formatAction(holiday.action)}</strong> holiday?
          <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
            {formatUtcDate(holiday.startDateTime)} - {formatUtcDate(holiday.endDateTime)}
          </div>
          <DialogFooter>
            <Button disabled={isDeleting} onClick={() => setConfirmOpen(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={isDeleting}
              onClick={() => deleteHoliday()}
              type="button"
              variant="destructive"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function defaultStart(): string {
  const now = new Date();
  // Start in the near future to avoid backend validation failures for past times.
  now.setMinutes(now.getMinutes() + 15);
  now.setSeconds(0, 0);
  const roundedMinutes = Math.ceil(now.getMinutes() / 15) * 15;
  now.setMinutes(roundedMinutes === 60 ? 0 : roundedMinutes);
  if (roundedMinutes === 60) {
    now.setHours(now.getHours() + 1);
  }
  return toDatetimeLocalInput(now);
}

function defaultEnd(start?: string): string {
  const base = start ? parseLocalDatetime(start) : parseLocalDatetime(defaultStart());
  base.setHours(base.getHours() + 1);
  return toDatetimeLocalInput(base);
}

function HolidayFormModal({
  open,
  onClose,
  lockId,
  holiday,
  copySourceHoliday,
  holidays,
  timezone,
}: {
  open: boolean;
  onClose: () => void;
  lockId: string;
  holiday: LockHolidayResponse | null;
  copySourceHoliday: LockHolidayResponse | null;
  holidays: LockHolidayResponse[];
  timezone: string;
}) {
  const queryClient = useQueryClient();
  const isEditing = holiday !== null;

  const [action, setAction] = useState("pass");
  const [startDateTime, setStartDateTime] = useState(defaultStart());
  const [endDateTime, setEndDateTime] = useState(defaultEnd());
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    if (holiday) {
      setAction(holiday.action ?? "pass");
      setStartDateTime(toDatetimeLocal(holiday.startDateTime) || defaultStart());
      setEndDateTime(toDatetimeLocal(holiday.endDateTime) || defaultEnd());
    } else if (copySourceHoliday) {
      const start = defaultStart();
      const sourceStart = parseDateInput(copySourceHoliday.startDateTime);
      const sourceEnd = parseDateInput(copySourceHoliday.endDateTime);
      const sourceDurationMs =
        sourceStart && sourceEnd
          ? sourceEnd.getTime() - sourceStart.getTime()
          : 3600000;

      const copiedEnd = parseLocalDatetime(start);
      copiedEnd.setTime(copiedEnd.getTime() + Math.max(sourceDurationMs, 900000));

      setAction(copySourceHoliday.action ?? "pass");
      setStartDateTime(start);
      setEndDateTime(toDatetimeLocalInput(copiedEnd));
    } else {
      const start = defaultStart();
      setAction("pass");
      setStartDateTime(start);
      setEndDateTime(defaultEnd(start));
    }
    setError(null);
  }, [holiday, copySourceHoliday, open]);

  const validationError = React.useMemo(() => {
    const start = parseLocalDatetime(startDateTime);
    const end = parseLocalDatetime(endDateTime);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "Please enter valid start and end dates.";
    }

    if (end <= start) {
      return "End date must be after start date.";
    }

    if (!isEditing && start.getTime() <= Date.now()) {
      return "Start date must be in the future.";
    }

    return null;
  }, [startDateTime, endDateTime, isEditing]);

  const overlapWarning = React.useMemo(() => {
    const start = parseLocalDatetime(startDateTime);
    const end = parseLocalDatetime(endDateTime);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    const hasOverlap = holidays.some((existing) => {
      if (isEditing && existing.id === holiday?.id) {
        return false;
      }

      const existingStart = parseDateInput(existing.startDateTime);
      const existingEnd = parseDateInput(existing.endDateTime);
      if (!existingStart || !existingEnd) {
        return false;
      }

      return start < existingEnd && end > existingStart;
    });

    return hasOverlap
      ? "This range overlaps an existing holiday. Please verify this is intentional."
      : null;
  }, [holidays, holiday?.id, isEditing, startDateTime, endDateTime]);

  const actionDescription = HOLIDAY_ACTION_DESCRIPTIONS[action] ?? "";

  const applyPreset = (preset: HolidayPresetKey) => {
    const range = getPresetRange(preset);
    setStartDateTime(range.start);
    setEndDateTime(range.end);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: LockHolidayRequest) =>
      isEditing
        ? axios.put(
            `${BASE_API_URL}/api/Locks/${lockId}/holidays/${holiday!.id}`,
            payload,
          )
        : axios.post(`${BASE_API_URL}/api/Locks/${lockId}/holidays`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY(lockId) });
      onClose();
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "An error occurred saving the holiday.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });

  const isSubmitDisabled = isPending || !!validationError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutate({
      action,
      startDateTime: parseLocalDatetime(startDateTime),
      endDateTime: parseLocalDatetime(endDateTime),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit Holiday" : "Create Holiday"}
            </DialogTitle>
          </DialogHeader>
          {error && (
            <Alert variant="destructive">
              {error}
            </Alert>
          )}
          {validationError && (
            <Alert variant="warning">
              {validationError}
            </Alert>
          )}
          {overlapWarning && (
            <Alert variant="info">
              {overlapWarning}
            </Alert>
          )}
          <div className="grid gap-2">
            <Label htmlFor="holiday-action">Action</Label>
            <Select
              value={action}
              onValueChange={setAction}
            >
              <SelectTrigger id="holiday-action" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOLIDAY_ACTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              {actionDescription}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {HOLIDAY_PRESETS.map((preset) => (
              <Button
                key={preset.key}
                onClick={() => applyPreset(preset.key)}
                size="sm"
                type="button"
                variant="outline"
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="holiday-start-date-time">Start Date & Time</Label>
            <Input
              id="holiday-start-date-time"
              type="datetime-local"
              value={startDateTime}
              onChange={(event) => setStartDateTime(event.target.value)}
              required
            />
            <span className="text-xs text-muted-foreground">Timezone: {timezone}</span>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="holiday-end-date-time">End Date & Time</Label>
            <Input
              id="holiday-end-date-time"
              type="datetime-local"
              value={endDateTime}
              onChange={(event) => setEndDateTime(event.target.value)}
              required
            />
            <span className="text-xs text-muted-foreground">Timezone: {timezone}</span>
          </div>
          <DialogFooter>
            <Button disabled={isPending} onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              {isPending ? "Saving..." : isEditing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function getLockHolidays(lockId: string): Promise<LockHolidayResponse[]> {
  const response = await axios.get<LockHolidayResponse[]>(
    `${BASE_API_URL}/api/Locks/${lockId}/holidays`,
  );
  return response.data;
}

function useGetLockHolidays(lockId: string) {
  return useQuery({
    queryKey: HOLIDAYS_QUERY_KEY(lockId),
    queryFn: () => getLockHolidays(lockId),
  });
}
